import React, { useState } from "react";
import { Users, Mail, RefreshCw, Eye, Edit, Trash2, X, Download } from "lucide-react";

const Admin = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState("");
  const [activeTab, setActiveTab] = useState("contacts");
  const [contacts, setContacts] = useState([]);
  const [trials, setTrials] = useState([]);
  const [unsubscribes, setUnsubscribes] = useState([]);
  const [searchEmail, setSearchEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selectedItem, setSelectedItem] = useState(null);
  const [modalType, setModalType] = useState(""); // 'view', 'edit', 'delete'
  const [editData, setEditData] = useState({});
  const [filters, setFilters] = useState({
    status: '',
    dateFrom: '',
    dateTo: '',
    sortBy: 'created',
    sortOrder: 'desc'
  });

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/sapadmin/login`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ password }),
        }
      );

      if (response.ok) {
        setIsAuthenticated(true);
        loadData();
      } else {
        setError("Invalid password");
      }
    } catch (err) {
      setError("Login failed");
    } finally {
      setLoading(false);
    }
  };

  const searchUnsubscribes = async (email) => {
    try {
      setLoading(true);
      const url = email 
        ? `${process.env.REACT_APP_API_URL}/sapadmin/unsubscribes?email=${encodeURIComponent(email)}`
        : `${process.env.REACT_APP_API_URL}/sapadmin/unsubscribes`;
      
      const response = await fetch(url);
      if (response.ok) {
        const data = await response.json();
        setUnsubscribes(data.unsubscribes || []);
      } else {
        setError('Failed to search unsubscribes');
      }
    } catch (err) {
      setError('Failed to search unsubscribes');
    } finally {
      setLoading(false);
    }
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    searchUnsubscribes(searchEmail);
  };

  const downloadTabData = (tabType) => {
    const today = new Date().toISOString().split('T')[0];
    let csv = '';
    let filename = '';
    
    if (tabType === 'contacts') {
      csv = 'Name,Email,Company,Phone,Subject,Message,Status,Comments,Follow-up Date,IP,Location,User Agent,Form Fill Time,Created,Updated\n' + 
        filteredContacts.map(c => `"${c.name || ''}","${c.email || ''}","${c.company || ''}","${c.phone || ''}","${c.subject || ''}","${c.message || ''}","${c.status || ''}","${c.comments || ''}","${c.followup_date || ''}","${c.ip_address || ''}","${c.location || ''}","${c.user_agent || ''}","${c.form_fill_time || ''}","${c.created || ''}","${c.updated || ''}"`).join('\n');
      filename = `contacts_${today}.csv`;
    } else if (tabType === 'trials') {
      csv = 'First Name,Last Name,Email,Company,Website,Employees,Use Case,Status,Comments,Follow-up Date,IP,Location,User Agent,Form Fill Time,Created,Updated\n' + 
        regularTrials.map(t => `"${t.firstName || ''}","${t.lastName || ''}","${t.email || ''}","${t.company || ''}","${t.website || ''}","${t.employees || ''}","${t.useCase || ''}","${t.status || ''}","${t.comments || ''}","${t.followup_date || ''}","${t.ip_address || ''}","${t.location || ''}","${t.user_agent || ''}","${t.form_fill_time || ''}","${t.created || ''}","${t.updated || ''}"`).join('\n');
      filename = `trials_${today}.csv`;
    } else if (tabType === 'homepage') {
      csv = 'Email,Status,Comments,Follow-up Date,IP,Location,User Agent,Form Fill Time,Created,Updated\n' + 
        homepageTrials.map(h => `"${h.email || ''}","${h.status || ''}","${h.comments || ''}","${h.followup_date || ''}","${h.ip_address || ''}","${h.location || ''}","${h.user_agent || ''}","${h.form_fill_time || ''}","${h.created || ''}","${h.updated || ''}"`).join('\n');
      filename = `homepage_${today}.csv`;
    } else if (tabType === 'unsubscribes') {
      csv = 'Email,Reason,Created,IP Address\n' + 
        filteredUnsubscribes.map(u => `"${u.email || ''}","${u.reason || ''}","${u.created || ''}","${u.ip_address || ''}"`).join('\n');
      filename = `unsubscribes_${today}.csv`;
    }
    
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const loadData = async () => {
    try {
      const [contactsRes, trialsRes, unsubscribesRes] = await Promise.all([
        fetch(`${process.env.REACT_APP_API_URL}/sapadmin/contacts`),
        fetch(`${process.env.REACT_APP_API_URL}/sapadmin/trials`),
        fetch(`${process.env.REACT_APP_API_URL}/sapadmin/unsubscribes`),
      ]);

      const contactsData = await contactsRes.json();
      const trialsData = await trialsRes.json();
      const unsubscribesData = await unsubscribesRes.json();

      setContacts(contactsData.contacts || []);
      setTrials(trialsData.trials || []);
      setUnsubscribes(unsubscribesData.unsubscribes || []);
    } catch (err) {
      setError("Failed to load data");
    }
  };

  const handleView = (item) => {
    setSelectedItem(item);
    setModalType('view');
  };

  const handleEdit = (item) => {
    setSelectedItem(item);
    setEditData(item);
    setModalType('edit');
  };

  const handleDelete = (item) => {
    setSelectedItem(item);
    setModalType('delete');
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      const endpoint = activeTab === 'contacts' ? 'contacts' : 'trials';
      const response = await fetch(`${process.env.REACT_APP_API_URL}/sapadmin/${endpoint}/${selectedItem.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(editData)
      });
      
      if (response.ok) {
        await loadData();
        setModalType('');
        setSelectedItem(null);
      } else {
        setError('Failed to update item');
      }
    } catch (err) {
      setError('Failed to update item');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteConfirm = async () => {
    try {
      setLoading(true);
      const endpoint = activeTab === 'contacts' ? 'contacts' : 'trials';
      const response = await fetch(`${process.env.REACT_APP_API_URL}/sapadmin/${endpoint}/${selectedItem.id}`, {
        method: 'DELETE'
      });
      
      if (response.ok) {
        await loadData();
        setModalType('');
        setSelectedItem(null);
      } else {
        setError('Failed to delete item');
      }
    } catch (err) {
      setError('Failed to delete item');
    } finally {
      setLoading(false);
    }
  };

  const closeModal = () => {
    setModalType('');
    setSelectedItem(null);
    setEditData({});
  };

  const clearFilters = () => {
    setFilters({
      status: '',
      dateFrom: '',
      dateTo: '',
      sortBy: 'created',
      sortOrder: 'desc'
    });
  };

  const filterAndSortData = (data) => {
    let filtered = [...data];
    
    // Filter by status
    if (filters.status) {
      filtered = filtered.filter(item => item.status === filters.status);
    }
    
    // Filter by date range
    if (filters.dateFrom) {
      filtered = filtered.filter(item => new Date(item.created) >= new Date(filters.dateFrom));
    }
    if (filters.dateTo) {
      filtered = filtered.filter(item => new Date(item.created) <= new Date(filters.dateTo));
    }
    
    // Sort data
    filtered.sort((a, b) => {
      const aValue = a[filters.sortBy];
      const bValue = b[filters.sortBy];
      
      if (filters.sortOrder === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });
    
    return filtered;
  };

  const filteredContacts = filterAndSortData(contacts);
  const filteredTrials = filterAndSortData(trials);
  const filteredUnsubscribes = filterAndSortData(unsubscribes);
  
  // Split trials into regular and homepage submissions
  const regularTrials = filteredTrials.filter(trial => !['homepage', 'Quick trial from homepage'].includes(trial.useCase));
  const homepageTrials = filteredTrials.filter(trial => ['homepage', 'Quick trial from homepage'].includes(trial.useCase));

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md w-full">
          <div className="card">
            <h1 className="text-2xl font-bold text-center mb-6">
              <span className="gradient-text">Admin Login</span>
            </h1>
            {error && (
              <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
                {error}
              </div>
            )}
            <form onSubmit={handleLogin}>
              <input
                type="password"
                placeholder="Enter admin password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent mb-4"
                required
              />
              <button
                type="submit"
                disabled={loading}
                className="w-full btn-primary"
              >
                {loading ? "Logging in..." : "Login"}
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold">
            <span className="gradient-text">Admin Dashboard</span>
          </h1>
          <p className="text-gray-600 mt-2">
            Manage contacts and trial accounts
          </p>
        </div>

        {/* Tabs */}
        <div className="mb-6">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8">
              <button
                onClick={() => setActiveTab("contacts")}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === "contacts"
                    ? "border-blue-500 text-blue-600"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }`}
              >
                <Mail className="inline h-4 w-4 mr-2" />
                Contacts ({filteredContacts.length})
              </button>
              <button
                onClick={() => setActiveTab("trials")}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === "trials"
                    ? "border-blue-500 text-blue-600"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }`}
              >
                <Users className="inline h-4 w-4 mr-2" />
                Trials ({regularTrials.length})
              </button>
              <button
                onClick={() => setActiveTab("homepage")}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === "homepage"
                    ? "border-blue-500 text-blue-600"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }`}
              >
                <Mail className="inline h-4 w-4 mr-2" />
                Homepage ({homepageTrials.length})
              </button>
              <button
                onClick={() => setActiveTab("unsubscribes")}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === "unsubscribes"
                    ? "border-blue-500 text-blue-600"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }`}
              >
                <X className="inline h-4 w-4 mr-2" />
                Unsubscribes ({filteredUnsubscribes.length})
              </button>
            </nav>
          </div>
        </div>

        {/* Filters */}
        <div className="mb-4 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-3">
            {activeTab !== 'unsubscribes' && (
              <select
                value={filters.status}
                onChange={(e) => setFilters({...filters, status: e.target.value})}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
              >
                <option value="">All Status</option>
                {activeTab === 'contacts' ? (
                  <>
                    <option value="new">New</option>
                    <option value="contacted">Contacted</option>
                    <option value="closed">Closed</option>
                    <option value="junk">Junk</option>
                  </>
                ) : (
                  <>
                    <option value="active">Active</option>
                    <option value="expired">Expired</option>
                    <option value="converted">Converted</option>
                    <option value="junk">Junk</option>
                  </>
                )}
              </select>
            )}
            
            <input
              type="date"
              value={filters.dateFrom}
              onChange={(e) => setFilters({...filters, dateFrom: e.target.value})}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
              placeholder="From Date"
            />
            
            <input
              type="date"
              value={filters.dateTo}
              onChange={(e) => setFilters({...filters, dateTo: e.target.value})}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
              placeholder="To Date"
            />
            
            <select
              value={`${filters.sortBy}-${filters.sortOrder}`}
              onChange={(e) => {
                const [sortBy, sortOrder] = e.target.value.split('-');
                setFilters({...filters, sortBy, sortOrder});
              }}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
            >
              <option value="created-desc">Date (Newest)</option>
              <option value="created-asc">Date (Oldest)</option>
              <option value="followup_date-asc">Follow-up (Earliest)</option>
              <option value="followup_date-desc">Follow-up (Latest)</option>
              <option value="status-asc">Status (A-Z)</option>
              <option value="status-desc">Status (Z-A)</option>
            </select>
            
            <button
              onClick={clearFilters}
              className="px-3 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm hover:bg-gray-200 transition-colors"
            >
              Clear Filters
            </button>
            
            <button
              onClick={loadData}
              disabled={loading}
              className="px-3 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 transition-colors flex items-center gap-2 justify-self-end"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
              {loading ? "Refreshing..." : "Refresh"}
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="card">
          {activeTab === "contacts" && (
            <div>
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-semibold">Contact Submissions</h2>
                <button
                  onClick={() => downloadTabData('contacts')}
                  className="btn-primary text-sm flex items-center gap-2"
                >
                  <Download className="h-4 w-4" />
                  Download CSV
                </button>
              </div>
              {filteredContacts.length === 0 ? (
                <p className="text-gray-500 text-center py-8">No contacts found</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                        <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                        <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                        <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                        <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {filteredContacts.map((contact) => (
                        <tr key={contact.id}>
                          <td className="px-3 py-4 text-sm font-medium text-gray-900">{contact.name}</td>
                          <td className="px-3 py-4 text-sm text-gray-500">{contact.email}</td>
                          <td className="px-3 py-4 text-sm text-gray-500">{contact.status}</td>
                          <td className="px-3 py-4 text-sm text-gray-500">{new Date(contact.created).toLocaleDateString()}</td>
                          <td className="px-3 py-4">
                            <div className="flex space-x-2">
                              <button onClick={() => handleView(contact)} className="text-blue-600"><Eye className="h-4 w-4" /></button>
                              <button onClick={() => handleEdit(contact)} className="text-green-600"><Edit className="h-4 w-4" /></button>
                              <button onClick={() => handleDelete(contact)} className="text-red-600"><Trash2 className="h-4 w-4" /></button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {activeTab === "trials" && (
            <div>
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-semibold">Trial Accounts</h2>
                <button
                  onClick={() => downloadTabData('trials')}
                  className="btn-primary text-sm flex items-center gap-2"
                >
                  <Download className="h-4 w-4" />
                  Download CSV
                </button>
              </div>
              {regularTrials.length === 0 ? (
                <p className="text-gray-500 text-center py-8">No trials found</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                        <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                        <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                        <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                        <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {regularTrials.map((trial) => (
                        <tr key={trial.id}>
                          <td className="px-3 py-4 text-sm font-medium text-gray-900">{trial.firstName} {trial.lastName}</td>
                          <td className="px-3 py-4 text-sm text-gray-500">{trial.email}</td>
                          <td className="px-3 py-4 text-sm text-gray-500">{trial.status}</td>
                          <td className="px-3 py-4 text-sm text-gray-500">{new Date(trial.created).toLocaleDateString()}</td>
                          <td className="px-3 py-4">
                            <div className="flex space-x-2">
                              <button onClick={() => handleView(trial)} className="text-blue-600"><Eye className="h-4 w-4" /></button>
                              <button onClick={() => handleEdit(trial)} className="text-green-600"><Edit className="h-4 w-4" /></button>
                              <button onClick={() => handleDelete(trial)} className="text-red-600"><Trash2 className="h-4 w-4" /></button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {activeTab === "homepage" && (
            <div>
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-semibold">Homepage Email Captures</h2>
                <button
                  onClick={() => downloadTabData('homepage')}
                  className="btn-primary text-sm flex items-center gap-2"
                >
                  <Download className="h-4 w-4" />
                  Download CSV
                </button>
              </div>
              {homepageTrials.length === 0 ? (
                <p className="text-gray-500 text-center py-8">No homepage submissions found</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                        <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                        <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                        <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {homepageTrials.map((trial) => (
                        <tr key={trial.id}>
                          <td className="px-3 py-4 text-sm font-medium text-gray-900">{trial.email}</td>
                          <td className="px-3 py-4 text-sm text-gray-500">{trial.status}</td>
                          <td className="px-3 py-4 text-sm text-gray-500">{new Date(trial.created).toLocaleDateString()}</td>
                          <td className="px-3 py-4">
                            <div className="flex space-x-2">
                              <button onClick={() => handleView(trial)} className="text-blue-600"><Eye className="h-4 w-4" /></button>
                              <button onClick={() => handleEdit(trial)} className="text-green-600"><Edit className="h-4 w-4" /></button>
                              <button onClick={() => handleDelete(trial)} className="text-red-600"><Trash2 className="h-4 w-4" /></button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {activeTab === "unsubscribes" && (
            <div>
              <h2 className="text-xl font-semibold mb-4 flex justify-between items-center">
                Unsubscribed Emails
                <button
                  onClick={() => downloadTabData('unsubscribes')}
                  className="btn-primary text-sm flex items-center gap-2"
                >
                  <Download className="h-4 w-4" />
                  Download CSV
                </button>
              </h2>
              
              {/* Search Form */}
              <form onSubmit={handleSearchSubmit} className="mb-4">
                <div className="flex gap-2">
                  <input
                    type="email"
                    placeholder="Search by email..."
                    value={searchEmail}
                    onChange={(e) => setSearchEmail(e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  />
                  <button
                    type="submit"
                    disabled={loading}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 transition-colors"
                  >
                    Search
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setSearchEmail('');
                      searchUnsubscribes('');
                    }}
                    className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm hover:bg-gray-200 transition-colors"
                  >
                    Clear
                  </button>
                </div>
              </form>
              {filteredUnsubscribes.length === 0 ? (
                <p className="text-gray-500 text-center py-8">
                  No unsubscribes found
                </p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                        <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Reason</th>
                        <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                        <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">IP Address</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {filteredUnsubscribes.map((unsubscribe, index) => (
                        <tr key={index}>
                          <td className="px-3 py-4 text-sm font-medium text-gray-900">{unsubscribe.email}</td>
                          <td className="px-3 py-4 text-sm text-gray-500">{unsubscribe.reason || 'No reason'}</td>
                          <td className="px-3 py-4 text-sm text-gray-500">{new Date(unsubscribe.created).toLocaleDateString()}</td>
                          <td className="px-3 py-4 text-sm text-gray-500">{unsubscribe.ip_address || 'N/A'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Modal */}
        {modalType && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 p-4">
            <div className="relative top-4 sm:top-20 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-bold">
                  {modalType === 'view' && 'View Details'}
                  {modalType === 'edit' && 'Edit Item'}
                  {modalType === 'delete' && 'Confirm Delete'}
                </h3>
                <button onClick={closeModal} className="text-gray-400 hover:text-gray-600">
                  <X className="h-6 w-6" />
                </button>
              </div>

              {modalType === 'view' && selectedItem && (
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {Object.entries(selectedItem).map(([key, value]) => (
                    <div key={key} className="flex flex-col sm:flex-row">
                      <span className="font-medium w-full sm:w-32 capitalize">{key.replace('_', ' ')}:</span>
                      <span className="flex-1 break-words">{value || 'N/A'}</span>
                    </div>
                  ))}
                </div>
              )}

              {modalType === 'edit' && selectedItem && (
                <div className="space-y-4 max-h-96 overflow-y-auto">
                  {activeTab === 'contacts' ? (
                    <>
                      <input type="text" placeholder="Name" value={editData.name || ''} onChange={(e) => setEditData({...editData, name: e.target.value})} className="w-full px-3 py-2 border rounded-lg" />
                      <input type="email" placeholder="Email" value={editData.email || ''} onChange={(e) => setEditData({...editData, email: e.target.value})} className="w-full px-3 py-2 border rounded-lg" />
                      <select value={editData.status || 'new'} onChange={(e) => setEditData({...editData, status: e.target.value})} className="w-full px-3 py-2 border rounded-lg">
                        <option value="new">New</option>
                        <option value="contacted">Contacted</option>
                        <option value="closed">Closed</option>
                        <option value="junk">Junk</option>
                      </select>
                    </>
                  ) : (
                    <>
                      <input type="text" placeholder="First Name" value={editData.firstName || ''} onChange={(e) => setEditData({...editData, firstName: e.target.value})} className="w-full px-3 py-2 border rounded-lg" />
                      <input type="text" placeholder="Last Name" value={editData.lastName || ''} onChange={(e) => setEditData({...editData, lastName: e.target.value})} className="w-full px-3 py-2 border rounded-lg" />
                      <input type="email" placeholder="Email" value={editData.email || ''} onChange={(e) => setEditData({...editData, email: e.target.value})} className="w-full px-3 py-2 border rounded-lg" />
                      <select value={editData.status || 'active'} onChange={(e) => setEditData({...editData, status: e.target.value})} className="w-full px-3 py-2 border rounded-lg">
                        <option value="active">Active</option>
                        <option value="expired">Expired</option>
                        <option value="converted">Converted</option>
                        <option value="junk">Junk</option>
                      </select>
                    </>
                  )}
                  <div className="flex justify-end space-x-2 mt-6">
                    <button onClick={closeModal} className="btn-secondary">Cancel</button>
                    <button onClick={handleSave} disabled={loading} className="btn-primary">{loading ? 'Saving...' : 'Save'}</button>
                  </div>
                </div>
              )}

              {modalType === 'delete' && selectedItem && (
                <div>
                  <p className="mb-4">Are you sure you want to delete this item? This action cannot be undone.</p>
                  <div className="flex justify-end space-x-2">
                    <button onClick={closeModal} className="btn-secondary">Cancel</button>
                    <button onClick={handleDeleteConfirm} disabled={loading} className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700">{loading ? 'Deleting...' : 'Delete'}</button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Admin;